import { useState, useEffect } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Card } from '@components/primitives/Card.js'
import { Badge } from '@components/primitives/Badge.js'
import { Input } from '@components/primitives/Input.js'
import { getDocumentos } from '@services/crud.js'
import type { Documento } from '@services/db.js'
import { formatCurrency, formatDate } from '@utils/format.js'
import { abrirRelatorioDespesas } from './relatorioDespesas.js'
import type { LancamentoItem } from './financeiroTypes.js'

function defaultRange() {
  const now = new Date()
  const de = new Date(now.getFullYear(), now.getMonth(), 1)
  const ate = now
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return {
    de: fmt(de),
    ate: fmt(ate),
    deLabel: de.toLocaleDateString('pt-BR'),
    ateLabel: ate.toLocaleDateString('pt-BR'),
  }
}

type Props = {
  lancamentos: LancamentoItem[]
  nomeObra: string
}

export function RelatoriosTab({ lancamentos, nomeObra }: Props) {
  const range = defaultRange()
  const [de, setDe] = useState(range.de)
  const [ate, setAte] = useState(range.ate)
  const [docs, setDocs] = useState<Documento[]>([])

  useEffect(() => {
    getDocumentos()
      .then(setDocs)
      .catch(() => {})
  }, [])

  const gastos = lancamentos.filter((l) => l.tipo !== 'receita')

  const despesasPeriodo = gastos
    .filter((l) => l.data >= de && l.data <= ate)
    .sort((a, b) => a.data.localeCompare(b.data))

  const totaisCat: Record<string, number> = {}
  for (const l of despesasPeriodo) {
    totaisCat[l.categoria] = (totaisCat[l.categoria] ?? 0) + l.valor
  }
  const totalPeriodo = despesasPeriodo.reduce((s, l) => s + l.valor, 0)

  const comNF = gastos.filter((l) => l.documentoId != null)
  const docMap = new Map(docs.map((d) => [d.id, d.nome]))
  const semNF = gastos.filter((l) => l.status === 'pago' && l.documentoId == null)

  return (
    <div className='cf3-fin-relatorios cf3-animate-fade-in'>
      <section className='cf3-block' aria-labelledby='rel-periodo-title'>
        <div className='cf3-fin-relatorios__header'>
          <h3 id='rel-periodo-title' className='cf3-subsection-title'>
            Despesas por período
          </h3>
          {despesasPeriodo.length > 0 && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => abrirRelatorioDespesas(lancamentos, de, ate, nomeObra)}
            >
              Imprimir / PDF
            </Button>
          )}
        </div>

        <div className='cf3-fin-relatorios__dates'>
          <Input
            name='de'
            label='De'
            type='date'
            value={de}
            onChange={(e) => setDe(e.target.value)}
          />
          <Input
            name='ate'
            label='Até'
            type='date'
            value={ate}
            onChange={(e) => setAte(e.target.value)}
          />
        </div>

        {despesasPeriodo.length === 0 ? (
          <p className='cf3-fin-relatorios__empty'>Nenhuma despesa no período selecionado.</p>
        ) : (
          <>
            <Card surface='raised'>
              <ul className='cf3-fin-cat-list'>
                {Object.entries(totaisCat)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, v]) => (
                    <li key={cat} className='cf3-fin-cat-item'>
                      <div className='cf3-fin-cat-item__row'>
                        <span className='cf3-fin-cat-item__nome'>{cat}</span>
                        <span className='cf3-fin-cat-item__valor'>{formatCurrency(v)}</span>
                      </div>
                    </li>
                  ))}
                <li className='cf3-fin-cat-item cf3-fin-cat-item--total'>
                  <div className='cf3-fin-cat-item__row'>
                    <strong>Total</strong>
                    <strong>{formatCurrency(totalPeriodo)}</strong>
                  </div>
                </li>
              </ul>
            </Card>
            <p className='cf3-fin-relatorios__count'>
              {despesasPeriodo.length} lançamento{despesasPeriodo.length !== 1 ? 's' : ''} no
              período
            </p>
          </>
        )}
      </section>

      <section className='cf3-block' aria-labelledby='rel-nf-title'>
        <h3 id='rel-nf-title' className='cf3-subsection-title'>
          Notas Fiscais Vinculadas ({comNF.length})
        </h3>
        {comNF.length === 0 ? (
          <p className='cf3-fin-relatorios__empty'>Nenhum lançamento com documento vinculado.</p>
        ) : (
          <Card surface='raised'>
            <div className='cf3-fin-dash__table-wrap'>
              <table className='cf3-fin-dash__table' aria-label='Notas fiscais vinculadas'>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Valor</th>
                    <th>Documento</th>
                  </tr>
                </thead>
                <tbody>
                  {comNF.map((l) => (
                    <tr key={l.id}>
                      <td className='cf3-fin-dash__td--date'>{formatDate(l.data)}</td>
                      <td className='cf3-fin-dash__td--titulo'>{l.titulo}</td>
                      <td className='cf3-fin-dash__td--cat'>{l.categoria}</td>
                      <td className='cf3-fin-dash__td--valor'>{formatCurrency(l.valor)}</td>
                      <td className='cf3-fin-dash__td--doc'>
                        {l.documentoId != null && docMap.has(l.documentoId) ? (
                          <span title='Documento vinculado'>Doc: {docMap.get(l.documentoId)}</span>
                        ) : (
                          <Badge label='NF vinculada' variant='success' size='sm' />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className='cf3-block' aria-labelledby='rel-falta-title'>
        <h3 id='rel-falta-title' className='cf3-subsection-title'>
          Comprovantes Faltando ({semNF.length})
        </h3>
        <p className='cf3-fin-relatorios__hint'>
          Gastos com status "Pago" sem documento vinculado.
        </p>
        {semNF.length === 0 ? (
          <p className='cf3-fin-relatorios__empty'>
            Todos os gastos pagos têm comprovante vinculado.
          </p>
        ) : (
          <Card surface='raised'>
            <div className='cf3-fin-dash__table-wrap'>
              <table className='cf3-fin-dash__table' aria-label='Comprovantes faltando'>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Fornecedor</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {semNF
                    .sort((a, b) => b.valor - a.valor)
                    .map((l) => (
                      <tr key={l.id}>
                        <td className='cf3-fin-dash__td--date'>{formatDate(l.data)}</td>
                        <td className='cf3-fin-dash__td--titulo'>{l.titulo}</td>
                        <td className='cf3-fin-dash__td--cat'>{l.categoria}</td>
                        <td className='cf3-fin-dash__td--forn'>{l.fornecedor ?? '—'}</td>
                        <td className='cf3-fin-dash__td--valor'>{formatCurrency(l.valor)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>
    </div>
  )
}
